import { Loader2 } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { toast } from '@apps/web/lib/app_notifications';

type RestartAnalysisConfirmModalProps = {
    readonly isOpen: boolean;
    readonly onClose: () => void;
    readonly onConfirm: () => Promise<boolean>;
    readonly title?: string;
    readonly description?: string;
    readonly confirmLabel?: string;
    readonly successMessage?: string;
    readonly errorMessage?: string;
    /**
     * The confirm handler already told the user what happened, so the modal
     * only closes. Needed when the outcome is not a plain succeeded/failed —
     * adopting a runner someone else restarted is a success the default
     * success copy would describe wrongly.
     */
    readonly reportsOwnOutcome?: boolean;
};

export function RestartAnalysisConfirmModal({
    isOpen,
    onClose,
    onConfirm,
    title = 'Restart analysis?',
    description = 'This clears all current suggestions and chat history for this outreach and starts the analysis over from scratch.',
    confirmLabel = 'Restart',
    successMessage = 'Analysis restarted',
    errorMessage = 'Failed to restart analysis',
    reportsOwnOutcome = false,
}: RestartAnalysisConfirmModalProps) {
    const [isSubmitting, setIsSubmitting] = useState(false);

    async function handleConfirm() {
        setIsSubmitting(true);
        try {
            const succeeded = await onConfirm();
            if (reportsOwnOutcome) {
                if (succeeded) {
                    onClose();
                }
                return;
            }
            if (succeeded) {
                toast.success(successMessage, {
                    persistNotification: true,
                    notificationType: 'app_feedback',
                });
                onClose();
            } else {
                toast.error(errorMessage, {
                    persistNotification: true,
                    notificationType: 'app_feedback',
                });
            }
        } finally {
            setIsSubmitting(false);
        }
    }

    function handleOpenChange(open: boolean) {
        if (!open && !isSubmitting) {
            onClose();
        }
    }

    return (
        <Dialog open={isOpen} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-[440px]">
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                    <DialogDescription>{description}</DialogDescription>
                </DialogHeader>
                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={onClose}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        onClick={handleConfirm}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : null}
                        {confirmLabel}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
